import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {SimpleSearchWithId, SearchByFilters, SearchByFiltersCustom} from "../../../../../reduxToolkit/Search/SearchApi";
import {useTranslation} from "react-i18next";
import CustomTh from "../../../../../components/table/CustomTh";
import CustomTd from "../../../../../components/table/CustomTd";
import CustomTooltip from "../../../../../components/CustomTooltip";
import CustomChip from "../../../../../components/CustomChip";
import {statusBorderColor, statusColor, statusFontColor} from "../../../../../enums/statusColorEnum";
import {status, STATUS_MODEL} from "../../../../../enums/statusEnum";
import DropDownMenuProfile from "../../../../../components/DropDown/DropDownMenuProfile";
import DropDownMenuItem from "../../../../../components/DropDown/DropDownMenuItem";
import routes from "../../../../../Routes/routes";
import {
    DetailsIconsOptions,
    IconClockTable,
    IconScheduleTable,
    NextIconsOptions,
    PersonOutlinedChip
} from "../../../../../components/buttons/IconButtons";
import TableFunction from "../../../../../components/table/TableFunction";
import {DATE_TIME_FORMAT_NAME_MODEL, errorMsg, getDateFormat, getTimeFormat} from "../../../../../Apis/functions";
import {
    CRITERIA_MODEL,
    SEARCH_MODEL
} from "../../../../../components/table/Model/TableModel";
import {VIEW_TYPE} from "../../../../../enums/ViewType";
import {useNavigate} from "react-router-dom";
import CustomImageTable from "../../../../../components/CustomImageTable";
import {USER_TYPE_NAME_MODEL} from "../../../../../enums/userTypeEnum";
import {permissionObj} from "../../../../../Helpers/permission";
import {ApprovalMaterialControl} from "../../../../../reduxToolkit/MaterialControl/MaterialControlApi";
import {
    getMaterialControlRequestTypeByType,
    getMaterialRequestTableStatusId,
    isVehicleMovement,
    MATERIAL_CONTROL_TABS,
    MATERIAL_CONTROL_APPROVAL_MODEL,
    SUB_VIEW_MODEL
} from "../../Model/Model";
import {colors} from "../../../../../enums/colors";
import {getToastMessageByStatus} from "../../../ApprovalTracker/Model/Model";
import {TABLES} from "../../../../../Apis/Tables";
import {toast} from "react-toastify";


/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
export const MaterialRequestTableApp = ({
    selectedList,
    setSelectedList,
    setIsAllChecked,
    option,
    setBodyForExport,
    searchElement,
    finalArray,
    modified,
    setModified,
    setCriteria,
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { permission, getUserByIdAtEntry, user } = useSelector((state) => state.authenticatioauthennSlice);
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const isGuard = user?.data?.userType?.name === USER_TYPE_NAME_MODEL.GUARD;
    const tableId = "tbl_mrt_material_requests";
    const searchId = "txt_mrt_search_material_requests";
    const [results, setResults] = useState(null);
    const deletePermission = false;
    const leftPosition = 0;
    // Requests still waiting for a decision are the ones approved in bulk
    const [listDisabled, setListDisabled] = useState([]);
    const [showApproveButton, setShowApproveButton] = useState(false);
    const [showApprovedModal, setShowApprovedModal] = useState(false);
    // The guard only registers the movements at the gate, so it does not approve
    const approvePermission = !isGuard && (DEV || permission?.includes(permissionObj?.WEB_MATERIAL_REQUEST_UPDATE));

    const getThead = () => {
        return [
            <CustomTh tableId={tableId} fixedXY={true} left={leftPosition} classes="first_head">{t('request')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('request_type')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('request_by')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('requested_date')}</CustomTh>,
            <CustomTh tableId={tableId} >{t('status')}</CustomTh>
        ];
    }

    /**
     * Tells whether a request can be selected for a bulk approval.
     *
     * A request is answered by the approvers of the flow it travels, so only the
     * one that is still waiting for a decision and that carries an answer of the
     * user reading it is offered: a user that takes part in the folio without
     * approving it reads the request without the action.
     *
     * @param {Object} item - Material request read from the table.
     *
     * @returns {boolean} True when the request can be approved.
     */
    const canApprove = (item) => {
        return approvePermission
            && item?.approvalStatusId === STATUS_MODEL.PENDING_REVIEW
            && item?.pendingApprovals > 0;
    }

    const getTbody = () => {
        if(!results || results?.content?.length === 0) return {
            body: [],
            data: []
        };

        let disabled = [];
        let body = results?.content?.map((item) => {
            // A request already approved is read with its own status
            const statusId = getMaterialRequestTableStatusId(item);
            const approvable = canApprove(item);
            // The request is read with the form or the vehicle movement it was registered with
            const form = getMaterialControlRequestTypeByType(item?.type);
            const classes = approvable ? 'box-shadow-secondary-400-top-bottom' : '';
            if(!approvable) disabled.push(item);

            return (
                [
                    <CustomTd tableId={tableId} fixed={true} left={leftPosition} classes={`first-column ${classes}`}>
                        {/* The request is read cut when it does not fit the column,
                            so it is displayed whole in the tooltip of the cell. */}
                        <CustomTooltip
                            text={`${item?.code} — ${item?.title}`}
                            placement={'top'}
                        >
                            <span className={"text-ellipsis text-ellipsis-cell"}>{`${item?.code} — ${item?.title}`}</span>
                        </CustomTooltip>
                    </CustomTd>,
                    <CustomTd tableId={tableId} classes={classes}>
                        <CustomChip
                            background={colors.secondary100}
                            fontColor={colors.secondary700}
                            borderColor={colors.secondary150}
                            legend={t(form.trans)}
                            icon={form.Icon}
                            classNameIcon={"icon-color-secondary-700 h-16px w-16px"}
                        />
                    </CustomTd>,
                    <CustomTd tableId={tableId} classes={classes}>
                        <CustomImageTable
                            tooltip={t(item?.requestedBy)}
                            containerClass={"d-flex"}
                            icon={<div className={`mr-2 d-flex`}><PersonOutlinedChip/></div>}
                        >
                            {t(item?.requestedBy)}
                        </CustomImageTable>
                    </CustomTd>,
                    // The request is registered at a moment of the day, so the
                    // cell reads the date it was asked for and the time it was
                    // registered at, each one with the icon that names it
                    <CustomTd tableId={tableId} classes={classes}>
                        <div className={'d-flex'}>
                            <IconScheduleTable className={'mr-8px mb-1 icon-color-general-950 fs-14px'}/>
                            <p className='fs-14px'>
                                {getDateFormat(item?.createdAt, null, DATE_TIME_FORMAT_NAME_MODEL.shortMonth)}
                            </p>
                        </div>
                        <div className={'d-flex'}>
                            <IconClockTable className={'mr-8px mb-1 icon-color-general-950'}/>
                            <p className='fs-14px'>
                                {getTimeFormat(item?.createdAt)}
                            </p>
                        </div>
                    </CustomTd>,
                    <CustomTd tableId={tableId} classes={classes}>
                        <CustomChip
                            background={statusColor[statusId]}
                            fontColor={statusFontColor[statusId]}
                            borderColor={statusBorderColor[statusId]}
                            legend={t(status[statusId])}
                            variant={'large'}
                        />
                    </CustomTd>,
                ]
            );
        });

        setListDisabled(disabled);

        return {
            body: body,
            data: results?.content?.map(e => {
                const approvable = canApprove(e);
                return {
                    ...e,
                    disabled: !approvable,
                    trClass: approvable ? 'background-secondary-50 background-hover-secondary-100' : '',
                    tdCheckboxClass: approvable ? 'box-shadow-secondary-400-top-bottom-left' : '',
                    tdActionsClass: approvable ? 'box-shadow-secondary-400-top-bottom-right' : ''
                };
            })
        }
    };

    const getActionsButtons = (item, index) => {
        // The form the request was registered with is the one its detail is read with
        const form = getMaterialControlRequestTypeByType(item?.type);

        return <>

                <DropDownMenuProfile
                    openMenu={openMenu}
                    handleMenuToggle={handleMenuToggle}
                    menuId={`menu-${item.id}`}
                >
                    {/* The requests tab is not read by the guard, so every request is
                        read with its tracker and with its own detail */}
                    {
                        [
                            // The tracker is only read when the request travels an approval flow
                            item?.approvalFlowId &&
                            <DropDownMenuItem
                                id={'btn_mrt_approval-tracker' + item.id}
                                index={index}
                                name={t('approval_tracker')}
                                url={{
                                    pathname: `${routes.approvalTracker.main}${TABLES.MATERIAL_CONTROL_REQUEST}/${item?.id}/${item?.approvalFlowId}`,
                                    state: {backUrl: {
                                        pathname: routes.materialControl.main,
                                        state: {
                                            currentTab: MATERIAL_CONTROL_TABS.REQUESTS.INDEX,
                                            currentSubView: SUB_VIEW_MODEL.REQUESTS.REQUEST
                                        }
                                    }}
                                }}
                                icon={<NextIconsOptions />}
                            />,
                            <DropDownMenuItem
                                id={`dmt_mrt_details_${item.id}`}
                                index={index}
                                handleClose={handleClose}
                                name={t('details')}
                                icon={<DetailsIconsOptions />}
                                url={{
                                    // A vehicle movement is read in its own detail and not in the one of the forms
                                    pathname: isVehicleMovement(item)
                                        ? routes.vehicleMovements.details + item?.id
                                        : routes.materialControl.details + item?.id,
                                    state: {
                                        formKey: form.key,
                                        backUrl: {
                                            pathname: routes.materialControl.main,
                                            state: {
                                                currentTab: MATERIAL_CONTROL_TABS.REQUESTS.INDEX,
                                                currentSubView: SUB_VIEW_MODEL.REQUESTS.REQUEST
                                            }
                                        }
                                    }
                                }}
                            />
                        ]
                    }
                </DropDownMenuProfile>

        </>;
    }

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page, criteria) => {
        const key = criteria?.searchElement !== undefined ? criteria?.searchElement : searchElement;
        const body = SEARCH_MODEL.setObject({
            page: page,
            size: rowsPerPage,
            model: option,
            key: key,
            userId: getUserByIdAtEntry?.userId,
            view: VIEW_TYPE.LIST.viewType,
        });

        setBodyForExport(body);

        dispatch(SimpleSearchWithId({ body })).then(
            ({
                 payload: {
                     data: { data, success },
                 },
             }) => {
                if (success === true) {
                    setResults(data);
                }
            },
        );
    }

    /**
     * Approves every request selected in the table.
     *
     * The approval service takes one request at a time, so the selection is sent
     * as one call per request and the table is read again once all of them end.
     *
     * @param {string} reason - Comment written by the approver.
     *
     * @returns {void}
     */
    const handleApprovalAll = (reason) => {
        const rows = results?.content?.filter(e => selectedList.includes(e.id)) || [];
        if(rows.length === 0) return;

        Promise.all(rows.map(e => dispatch(ApprovalMaterialControl(MATERIAL_CONTROL_APPROVAL_MODEL.toRegister({
            id: e?.id,
            approvalStatusId: STATUS_MODEL.APPROVED,
            approvalReason: reason || null
        }))))).then((responses) => {
            const failed = responses.find(({ payload: { data: { success } } }) => !success);

            setSelectedList([]);
            setListDisabled([]);
            setIsAllChecked(false);
            setModified(true);

            if (failed) errorMsg(failed?.payload?.data?.code);
            else toast.success(t(getToastMessageByStatus(STATUS_MODEL.APPROVED)));
        });
    };

    /**
     * Executes a paginated search request using the filters of the advanced search.
     *
     * The filters are read with the same model as the simple search and scoped
     * to the same user, so the table keeps listing the folios the user takes part in.
     */
    const getDataAdvancedSearch = (page) => {
        const body = SEARCH_MODEL.setObject({
            page: page,
            size: rowsPerPage,
            model: option,
            rootGroup: CRITERIA_MODEL.set(finalArray),
            // The search scoped to a user is answered by the custom search, the plain
            // one only takes a search without the user to find
            userToFindId: getUserByIdAtEntry?.userId,
            view: VIEW_TYPE.LIST.viewType,
        });

        setCriteria(body);

        dispatch((body.userToFindId ? SearchByFiltersCustom : SearchByFilters)({ option, body })).then(
            ({
                 payload: {
                     data: { data, success },
                 },
             }) => {
                if (success === true) {
                    setResults(data);
                }
            },
        );
    };

    const {
        thead,
        tbody,
        handleCheckboxChange,
        handleDeleteAll,
        openMenu,
        handleMenuToggle,
        handleClose,
        pagination,
        rowsPerPage
    } = TableFunction({
        selectedList,
        setSelectedList,
        data:results,
        getThead,
        getTbody,
        labelRowsPerPage:'requests_per_page',
        fixedPagination: false,
        hasContainer:true,
        setIsAllChecked,
        searchElement, //New
        getData,//New
        getDataAdvancedSearch,
        finalArray,
        modified,//New
        setModified,//New
    });

    // Enable approve button only when items are selected
    useEffect(() => {
        setShowApproveButton(selectedList?.length > 0);
    }, [selectedList]);

    return {
        t,
        navigate,
        handleDeleteAll,
        permission,
        DEV,
        tableId,
        searchId,
        thead,
        tbody,
        handleCheckboxChange,
        getActionsButtons,
        pagination,
        rowsPerPage,
        results,
        setResults,
        deletePermission,
        listDisabled,
        showApproveButton,
        showApprovedModal,
        setShowApprovedModal,
        handleApprovalAll,
        getData
    }
};
